import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import useShare from '@/src/Share/components/hooks/useShare';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import BulkUploadMedia from '@/src/Common/components/BulkUploadMedia';

/*
 * Session v2 — one guest's session (the classic Session Manager,
 * de-bridged): its photos, its shares with add + send, upload into
 * the session, copy the guest link. "New session" is just a fresh
 * client-side UUID — the session exists once photos or shares
 * reference it (classic semantics).
 */

export default function SessionV2() {
    const navigate = useNavigate();
    const { activationId, photoSessionId } = useParams();
    const id = activationId ? +activationId : 0;
    const isNew = photoSessionId == null;
    const [sessionId] = useState<string>(
        () => photoSessionId ?? crypto.randomUUID(),
    );
    const { getSessionPhotos } = useActivation();
    const { getGalleriesForActivation } = useGetGalleries();
    const {
        getSharesForActivation,
        sendUnsentSharesForSession,
        addShareToSession,
    } = useShare();
    const authenticatedApi = useAuthenticatedApi();

    const [photos, setPhotos] = useState<any[]>([]);
    const [shares, setShares] = useState<any[]>([]);
    const [galleryId, setGalleryId] = useState<number | null>(null);
    const [showHidden, setShowHidden] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [newDest, setNewDest] = useState('');
    const [addBusy, setAddBusy] = useState(false);

    const load = async () => {
        const [p, allShares, galleries] = await Promise.all([
            getSessionPhotos(id, sessionId).catch(() => []),
            getSharesForActivation(id).catch(() => []),
            getGalleriesForActivation(id).catch(() => []),
        ]);
        setPhotos(p ?? []);
        const seen = new Set();
        setShares(
            (allShares ?? []).filter((s: any) => {
                if (s.photoSessionId !== sessionId) return false;
                if (s.id == null || seen.has(s.id)) return false;
                seen.add(s.id);
                return true;
            }),
        );
        setGalleryId(galleries?.[0]?.id ?? null);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, sessionId]);

    const visible = useMemo(
        () => photos.filter((p) => showHidden || !p.isHidden),
        [photos, showHidden],
    );
    const hiddenCount = photos.filter((p) => p.isHidden).length;
    const unsent = shares.filter((s) => !s.sent).length;
    const sessionTime = photos[0]?.capturedAt ?? photos[0]?.createdAt;

    const toggleHidden = async (m: any) => {
        setPhotos((rows) =>
            rows.map((r) =>
                r.id === m.id ? { ...r, isHidden: !m.isHidden } : r,
            ),
        );
        await authenticatedApi
            .put(`media/${m.id}/visibility`, { isHidden: !m.isHidden })
            .catch(() => null);
    };

    const addShare = async () => {
        const dest = newDest.trim();
        if (!dest || addBusy || galleryId == null) return;
        setAddBusy(true);
        await Promise.resolve(
            addShareToSession(
                sessionId,
                photos[0]?.shareIdentifier ?? sessionId,
                dest,
                id,
                galleryId,
            ),
        ).catch(() => null);
        setNewDest('');
        await load();
        setAddBusy(false);
    };

    const send = async () => {
        if (isSending) return;
        setIsSending(true);
        await Promise.resolve(
            sendUnsentSharesForSession(sessionId),
        ).catch(() => null);
        await load();
        setIsSending(false);
    };

    const copyLink = () => {
        if (galleryId == null) return;
        navigator.clipboard
            ?.writeText(
                `${window.location.origin}/session/${galleryId}/${sessionId}`,
            )
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            });
    };

    if (isLoading) {
        return (
            <main className="mx-auto max-w-[860px] px-[clamp(18px,3vw,34px)] pt-4">
                <div className="grid grid-cols-4 gap-2">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div
                            key={i}
                            className="aspect-square animate-pulse rounded-[10px] bg-secondary"
                        />
                    ))}
                </div>
            </main>
        );
    }

    return (
        <main className="mx-auto max-w-[860px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-4">
                <button
                    type="button"
                    onClick={() => navigate(`/v2/activations/${id}/insights`)}
                    className="ps-label cursor-pointer border-0 bg-transparent p-0 hover:text-foreground"
                >
                    Insights
                </button>
                <span className="mx-1.5 opacity-60">/</span>
                <span className="text-muted-foreground">
                    {isNew ? 'New session' : 'Session'}
                </span>
            </div>

            {/* header */}
            <div className="mb-6 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                    <h1 className="text-[17px] font-bold tracking-[-0.01em]">
                        {visible.length} photo
                        {visible.length === 1 ? '' : 's'}
                        {sessionTime &&
                            ` · ${moment(sessionTime).format(
                                'MMM D, h:mm A',
                            )}`}
                    </h1>
                    <div className="mt-0.5 font-mono text-[10.5px] text-faint">
                        {sessionId}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={copyLink}
                    className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-foreground"
                >
                    {copied ? 'Copied ✓' : 'Copy guest link'}
                </button>
                <button
                    type="button"
                    onClick={() => setUploadOpen(true)}
                    className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-foreground"
                >
                    Upload
                </button>
                {unsent > 0 && (
                    <button
                        type="button"
                        disabled={isSending}
                        onClick={send}
                        className="cursor-pointer rounded-full border-0 bg-brand px-4 py-2 font-[inherit] text-[12.5px] font-semibold text-white"
                    >
                        {isSending
                            ? 'Sending…'
                            : `Send ${unsent} unsent share${
                                  unsent === 1 ? '' : 's'
                              }`}
                    </button>
                )}
            </div>

            {/* photos */}
            <section className="mb-9">
                <div className="mb-1.5 flex items-baseline justify-between">
                    <span className="ps-label">Photos</span>
                    {hiddenCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setShowHidden((h) => !h)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-foreground"
                        >
                            {showHidden
                                ? 'Hide hidden'
                                : `Show ${hiddenCount} hidden`}
                        </button>
                    )}
                </div>
                {visible.length === 0 && (
                    <p className="py-3 text-[13px] text-muted-foreground">
                        No photos yet — upload some, or they arrive as the
                        guest captures.
                    </p>
                )}
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                    {visible.map((m) => (
                        <div
                            key={m.id}
                            className="group relative aspect-square overflow-hidden rounded-[10px] bg-secondary"
                        >
                            <img
                                src={
                                    m.thumbnailUri ??
                                    m.thumbnailTinyUri ??
                                    m.originalUri
                                }
                                alt=""
                                loading="lazy"
                                className={
                                    'h-full w-full object-cover ' +
                                    (m.isHidden ? 'opacity-40' : '')
                                }
                            />
                            {m.isHidden && (
                                <span className="absolute left-1.5 top-1.5 rounded-[5px] bg-secondary px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-[0.06em] text-muted-foreground">
                                    Hidden
                                </span>
                            )}
                            <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                <button
                                    type="button"
                                    title={m.isHidden ? 'Show' : 'Hide'}
                                    onClick={() => toggleHidden(m)}
                                    className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-[7px] border-0 bg-black/45 text-[11px] text-white"
                                >
                                    {m.isHidden ? '◎' : '⊘'}
                                </button>
                                <button
                                    type="button"
                                    title="Open original"
                                    onClick={() =>
                                        window.open(m.originalUri, '_blank')
                                    }
                                    className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-[7px] border-0 bg-black/45 text-[11px] text-white"
                                >
                                    ⤢
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* shares */}
            <section className="mb-9">
                <div className="ps-label mb-1.5">Shares</div>
                {shares.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        No shares on this session yet.
                    </p>
                )}
                {shares.map((s: any, i: number) => (
                    <div
                        key={s.id}
                        className={
                            'flex items-center gap-3 py-2 text-[12.5px] ' +
                            (i > 0 ? 'border-t border-line' : '')
                        }
                    >
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {s.destination || '—'}
                        </span>
                        <span className="font-mono text-[10px] uppercase text-faint">
                            {s.type === 'text' ? 'SMS' : s.type}
                        </span>
                        <span
                            className={
                                'text-[11px] font-semibold ' +
                                (s.sent ? 'text-good' : 'text-attention')
                            }
                        >
                            {s.sent ? 'sent' : 'unsent'}
                        </span>
                    </div>
                ))}
                <div className="mt-2.5 flex items-center gap-2.5">
                    <input
                        className="ps-input w-[240px] text-[13px]"
                        placeholder="Add email or phone…"
                        value={newDest}
                        onChange={(e) => setNewDest(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') addShare();
                        }}
                    />
                    <button
                        type="button"
                        disabled={addBusy || !newDest.trim()}
                        onClick={addShare}
                        className="cursor-pointer rounded-full border border-border bg-transparent px-3.5 py-[7px] font-[inherit] text-[12px] font-semibold text-foreground hover:border-faint disabled:opacity-50"
                    >
                        {addBusy ? 'Adding…' : 'Add share'}
                    </button>
                    <span className="text-[11px] text-faint">
                        added unsent — use Send to deliver
                    </span>
                </div>
            </section>

            {/* upload overlay */}
            {uploadOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setUploadOpen(false)}
                >
                    <div
                        className="max-h-[80vh] w-full max-w-[560px] overflow-y-auto rounded-[15px] border border-border bg-background p-5 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <BulkUploadMedia
                            activationId={id}
                            galleryId={galleryId}
                            photoSessionId={sessionId}
                            onCancel={() => setUploadOpen(false)}
                            onComplete={() => {
                                setUploadOpen(false);
                                load();
                            }}
                        />
                    </div>
                </div>
            )}
        </main>
    );
}
